import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  clearPlaybackPosition,
  DEFAULT_PLAYBACK_SPEED,
  readPlaybackPosition,
  readPlaybackSpeed,
  shouldClearPlaybackPosition,
  shouldPersistPlaybackPosition,
  writePlaybackPosition,
  writePlaybackSpeed,
} from './podcastPlaybackStorage';
import { useHydrated } from './useHydrated';

export type AudioControls = {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isPlaying: boolean;
  isReady: boolean;
  isBuffering: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  srcAttached: boolean;
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
  togglePlay: () => void;
  handleRewind: () => void;
  handleForward: () => void;
  seekTo: (ratio: number) => void;
};

function getSavedPosition(playbackStorageKey: string | undefined, knownDuration: number): number | null {
  if (!playbackStorageKey) {
    return null;
  }

  const saved = readPlaybackPosition(playbackStorageKey);
  if (saved == null || !shouldPersistPlaybackPosition(saved, knownDuration)) {
    return null;
  }

  return saved;
}

/** How often to write progress during continuous playback (hard-crash backstop). */
export const PLAYBACK_PERSIST_INTERVAL_MS = 5_000;

export function useAudioPlayer({
  rewindSeconds = 10,
  forwardSeconds = 15,
  knownDuration,
  playbackStorageKey,
  enablePlaybackSpeed = false,
}: {
  rewindSeconds?: number;
  forwardSeconds?: number;
  knownDuration?: number;
  playbackStorageKey?: string;
  enablePlaybackSpeed?: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const hydrated = useHydrated();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(knownDuration != null && knownDuration > 0);
  const [isBuffering, setIsBuffering] = useState(false);
  const [duration, setDuration] = useState(knownDuration ?? 0);
  const [srcAttached, setSrcAttached] = useState(knownDuration == null || knownDuration <= 0);
  const [liveTime, setLiveTime] = useState<number | null>(null);
  const [playbackRate, setPlaybackRateState] = useState(DEFAULT_PLAYBACK_SPEED);

  const savedPosition = useMemo(() => {
    if (!hydrated) {
      return null;
    }

    return getSavedPosition(playbackStorageKey, knownDuration ?? 0);
  }, [hydrated, playbackStorageKey, knownDuration]);

  // Track the target time during seeks so the UI doesn't flash to 0:00
  const seekTargetRef = useRef<number | null>(null);
  const hasRestoredRef = useRef(false);
  const lastPersistedAtRef = useRef<number | null>(null);
  const activeKeyRef = useRef(playbackStorageKey);
  const playbackStorageKeyRef = useRef(playbackStorageKey);
  playbackStorageKeyRef.current = playbackStorageKey;
  const durationRef = useRef(duration);
  durationRef.current = duration;
  const playbackRateRef = useRef(playbackRate);
  playbackRateRef.current = playbackRate;

  if (activeKeyRef.current !== playbackStorageKey) {
    activeKeyRef.current = playbackStorageKey;
    setLiveTime(null);
    hasRestoredRef.current = false;
    lastPersistedAtRef.current = null;
  }

  const currentTime = liveTime ?? savedPosition ?? 0;
  if (liveTime == null) {
    seekTargetRef.current = savedPosition;
  }

  const persistPosition = useCallback(() => {
    const key = playbackStorageKeyRef.current;
    const audio = audioRef.current;
    if (!key || !audio) {
      return;
    }

    const position = audio.currentTime;
    const audioDuration = audio.duration || durationRef.current;

    if (shouldClearPlaybackPosition(position, audioDuration)) {
      clearPlaybackPosition(key);
      lastPersistedAtRef.current = Date.now();
      return;
    }

    if (!shouldPersistPlaybackPosition(position, audioDuration)) {
      if (hasRestoredRef.current) {
        clearPlaybackPosition(key);
        lastPersistedAtRef.current = Date.now();
      }
      return;
    }

    writePlaybackPosition(key, position);
    lastPersistedAtRef.current = Date.now();
  }, []);

  const maybePersistPosition = useCallback(() => {
    const lastPersistedAt = lastPersistedAtRef.current;
    if (lastPersistedAt != null && Date.now() - lastPersistedAt < PLAYBACK_PERSIST_INTERVAL_MS) {
      return;
    }

    persistPosition();
  }, [persistPosition]);

  const restorePosition = useCallback((audio: HTMLAudioElement, audioDuration: number) => {
    const key = playbackStorageKeyRef.current;
    if (!key || hasRestoredRef.current) {
      return;
    }

    hasRestoredRef.current = true;

    const saved = readPlaybackPosition(key);
    if (saved == null) {
      seekTargetRef.current = null;
      return;
    }

    if (!shouldPersistPlaybackPosition(saved, audioDuration)) {
      clearPlaybackPosition(key);
      seekTargetRef.current = null;
      audio.currentTime = 0;
      setLiveTime(0);
      return;
    }

    seekTargetRef.current = saved;
    setLiveTime(saved);
    audio.currentTime = saved;
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      // While seeking, hold the display at the target time until the browser catches up
      if (seekTargetRef.current != null) {
        if (Math.abs(audio.currentTime - seekTargetRef.current) < 1) {
          seekTargetRef.current = null;
        } else {
          setLiveTime(seekTargetRef.current);
          return;
        }
      }
      setLiveTime(audio.currentTime);
      maybePersistPosition();
    };
    const onLoadedMetadata = () => {
      const audioDuration = audio.duration || 0;
      audio.playbackRate = playbackRateRef.current;
      setDuration(audioDuration);
      setIsReady(true);
      restorePosition(audio, audioDuration);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      setIsPlaying(false);
      setIsBuffering(false);
      persistPosition();
    };
    const onEnded = () => {
      setIsPlaying(false);
      setIsBuffering(false);
      const key = playbackStorageKeyRef.current;
      if (key) {
        clearPlaybackPosition(key);
      }
    };
    const onWaiting = () => setIsBuffering(true);
    const onCanPlay = () => setIsBuffering(false);
    const onPlaying = () => setIsBuffering(false);
    const onSeeked = () => {
      seekTargetRef.current = null;
      setLiveTime(audio.currentTime);
      setIsBuffering(false);
      persistPosition();
    };
    const onError = () => {
      setIsBuffering(false);
    };

    // Check if metadata already loaded (race condition fix)
    // readyState >= 1 means HAVE_METADATA or higher
    if (audio.readyState >= 1) {
      onLoadedMetadata();
    }

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('seeked', onSeeked);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('seeked', onSeeked);
      audio.removeEventListener('error', onError);
    };
  }, [maybePersistPosition, persistPosition, restorePosition]);

  // Persist before DOM teardown so SPA navigations don't lose the last few seconds.
  // useEffect cleanups run too late — audioRef is already null by then.
  useLayoutEffect(() => {
    if (!playbackStorageKey) {
      return;
    }

    return () => {
      persistPosition();
    };
  }, [playbackStorageKey, persistPosition]);

  useEffect(() => {
    if (!playbackStorageKey) {
      return;
    }

    const onPageHide = () => {
      persistPosition();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        persistPosition();
      }
    };

    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [playbackStorageKey, persistPosition]);

  useEffect(() => {
    if (!enablePlaybackSpeed) {
      return;
    }

    setPlaybackRateState(readPlaybackSpeed());
  }, [enablePlaybackSpeed]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    audio.playbackRate = playbackRate;
  }, [playbackRate]);

  const setPlaybackRate = useCallback((rate: number) => {
    setPlaybackRateState(rate);
    writePlaybackSpeed(rate);
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!audio.paused) {
      audio.pause();
      return;
    }

    // First user interaction: allow source element to render, then start loading
    if (!srcAttached) {
      setSrcAttached(true);
    }

    setIsBuffering(true);
    audio
      .play()
      .then(() => {
        setIsPlaying(true);
        setIsBuffering(false);
      })
      .catch(() => {
        setIsPlaying(false);
        setIsBuffering(false);
      });
  }, [srcAttached]);

  const handleRewind = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !srcAttached) return;
    const target = Math.max(0, audio.currentTime - rewindSeconds);
    seekTargetRef.current = target;
    setLiveTime(target);
    audio.currentTime = target;
  }, [rewindSeconds, srcAttached]);

  const handleForward = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !srcAttached) return;
    const target = Math.min(audio.duration || 0, audio.currentTime + forwardSeconds);
    seekTargetRef.current = target;
    setLiveTime(target);
    audio.currentTime = target;
  }, [forwardSeconds, srcAttached]);

  const seekTo = useCallback(
    (ratio: number) => {
      const audio = audioRef.current;
      if (!audio || !duration || !srcAttached) return;
      const target = ratio * duration;
      seekTargetRef.current = target;
      setLiveTime(target);
      audio.currentTime = target;
    },
    [duration, srcAttached],
  );

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return {
    audioRef,
    isPlaying,
    isReady,
    isBuffering,
    currentTime,
    duration,
    progress,
    srcAttached,
    playbackRate,
    setPlaybackRate,
    togglePlay,
    handleRewind,
    handleForward,
    seekTo,
  };
}
